/**
 * "Home" camera framing (pure; tested in test/framing.test.ts): our territory plus the
 * coverage of our radars, fitted into the screen area the HUD leaves free, so the nation
 * reads at a comfortable size — not a 4% speck in a sea of fog. Late in a match a nation can
 * own scattered pieces: the frame keeps to the capital's landmass (plus enclaves big enough to
 * matter); if even that does not fit, a big nation is shown as wide as the camera goes (when the caller
 * allows zooming past its usual minimum), else the frame centres on the capital.
 */
import { BALANCE, getGeo, landHexAt, type PlayerView, type WorldMap } from '@sa/sim';

/** HUD safe zones (CSS px): top bar, bottom build strip, sides. */
export const HUD_TOP_PX = 80;
export const HUD_BOTTOM_PX = 110;
export const HUD_SIDE_PX = 40;
/** extra room around the framed box */
const MARGIN = 1.12;
/** never frame closer than this (css px per mu): neighbours stay in sight */
export const HOME_MAX_ZOOM = 26;
/** pieces of our land smaller than this (hexes) are not worth framing */
export const ENCLAVE_MIN_HEXES = 5;
/** the camera would push the frame off-centre by more than this share of the screen: zoom in instead */
const MAX_EDGE_SHIFT = 0.12;

export interface Frame {
  x: number;
  y: number;
  zoom: number;
}

/** Connected pieces of our land (land neighbours), as hex lists, biggest first. */
function ownPieces(view: PlayerView, world: WorldMap): number[][] {
  const seen = new Uint8Array(world.landCount);
  const pieces: number[][] = [];
  for (let s = 0; s < world.landCount; s++) {
    if (seen[s] || view.owner[s] !== view.me) continue;
    const piece = [s];
    seen[s] = 1;
    for (let i = 0; i < piece.length; i++) {
      const h = piece[i];
      for (let k = 0; k < 6; k++) {
        const n = world.nbr[h * 6 + k];
        if (n < 0 || seen[n] || view.owner[n] !== view.me) continue;
        seen[n] = 1;
        piece.push(n);
      }
    }
    pieces.push(piece);
  }
  return pieces.sort((a, b) => b.length - a.length);
}

/**
 * Camera centre + zoom that frames our land and radar cover in a w×h viewport, or null.
 * `capitalHex`: our capital (its landmass is the one framed first), −1 if unknown.
 * `minZoom` keeps the usual home view from zooming out too far; a big late-game nation that
 * does not fit there may go out to `floorZoom` (the camera's own limit) to be seen whole.
 */
export function homeFrame(view: PlayerView, world: WorldMap, w: number, h: number, minZoom: number, capitalHex = -1, floorZoom = minZoom): Frame | null {
  const pieces = ownPieces(view, world);
  if (!pieces.length) return null;
  const main = pieces.find((p) => p.includes(capitalHex)) ?? pieces[0];
  const uw = Math.max(1, w - 2 * HUD_SIDE_PX);
  const uh = Math.max(1, h - HUD_TOP_PX - HUD_BOTTOM_PX);
  const geo = getGeo(world);
  // centre the box in the free area (the bottom strip is taller than the top bar)
  const shiftPx = (HUD_BOTTOM_PX - HUD_TOP_PX) / 2;

  const fit = (hexes: number[]): { box: [number, number, number, number]; zoom: number } => {
    const inside = new Set(hexes);
    let x0 = Infinity;
    let y0 = Infinity;
    let x1 = -Infinity;
    let y1 = -Infinity;
    const grow = (x: number, y: number, r: number) => {
      x0 = Math.min(x0, x - r);
      y0 = Math.min(y0, y - r);
      x1 = Math.max(x1, x + r);
      y1 = Math.max(y1, y + r);
    };
    for (const i of hexes) grow(world.cx[i], world.cy[i], world.hexR);
    for (const e of view.entities.values()) {
      if (e.owner === view.me && e.kind === 'radar' && e.state !== 'building' && inside.has(landHexAt(geo, e.x, e.y))) grow(e.x, e.y, BALANCE.vision.radar);
    }
    return { box: [x0, y0, x1, y1], zoom: Math.min(uw / ((x1 - x0) * MARGIN), uh / ((y1 - y0) * MARGIN)) };
  };
  // The camera keeps its view over the map, so near the map's edge a wide view cannot be centred
  // on a point: the nation would end up crammed against the screen edge (late game, big nations).
  const edgeShift = (x: number, y: number, zoom: number): number => {
    const axis = (c: number, half: number, size: number) => (half * 2 >= size ? Math.abs(size / 2 - c) : Math.max(0, half - c, c - (size - half)));
    return Math.max((axis(x, w / 2 / zoom, world.width) * zoom) / w, (axis(y, h / 2 / zoom, world.height) * zoom) / h);
  };
  const clampZoom = (z: number, lo = minZoom) => Math.max(lo, Math.min(HOME_MAX_ZOOM, z));
  /** The box framed whole and centred, or null if it does not fit or the camera could not centre it. */
  const whole = (b: { box: [number, number, number, number]; zoom: number }, lo = minZoom): Frame | null => {
    if (b.zoom < lo) return null;
    const zoom = clampZoom(b.zoom, lo);
    const x = (b.box[0] + b.box[2]) / 2;
    const y = (b.box[1] + b.box[3]) / 2;
    return edgeShift(x, y, zoom) <= MAX_EDGE_SHIFT ? { x, y: y + shiftPx / zoom, zoom } : null;
  };

  // the capital's landmass plus enclaves that matter, else just the capital's landmass…
  const core = fit(main);
  const all = fit([...main, ...pieces.filter((p) => p !== main && p.length >= ENCLAVE_MIN_HEXES).flat()]);
  const f = whole(all) ?? whole(core) ?? (floorZoom < minZoom ? (whole(all, floorZoom) ?? whole(core, floorZoom)) : null);
  if (f) return f;
  // too big even for that: as wide as the camera goes, on the middle of the landmass (the camera
  // keeps the view over the map) — the whole nation in sight beats a capital close-up
  if (floorZoom < minZoom) return { x: (core.box[0] + core.box[2]) / 2, y: (core.box[1] + core.box[3]) / 2, zoom: floorZoom };
  // …else centre on the capital, as wide as the landmass asks, zoomed in just enough for the
  // camera to centre it there
  const c = main.includes(capitalHex) ? capitalHex : main[0];
  const x = world.cx[c];
  const y = world.cy[c];
  let zoom = clampZoom(core.zoom);
  if (edgeShift(x, y, zoom) > MAX_EDGE_SHIFT) {
    const need = Math.max(w / 2 / Math.max(1e-3, Math.min(x, world.width - x)), h / 2 / Math.max(1e-3, Math.min(y, world.height - y)));
    zoom = Math.max(zoom, Math.min(HOME_MAX_ZOOM, need));
  }
  return { x, y: y + shiftPx / zoom, zoom };
}

/** Space on a threat never frames wider than this (css px per mu): the city must read */
export const THREAT_MIN_ZOOM = 8;
/** …nor closer than this */
export const THREAT_MAX_ZOOM = 30;
/** share of the free area the target + missile head may fill (20% margin on each side) */
const THREAT_FILL = 0.6;

/**
 * The threat frame (Space, the alert line): the impact point and the missile head with 20%
 * margins, at least `minZoom` (home × 1.3). A head too far out to fit at that zoom stays off
 * screen (its edge chevron points at it): the frame leans towards it as far as the target can
 * stay inside the margins.
 */
export function threatFrame(target: { x: number; y: number }, head: { x: number; y: number }, w: number, h: number, minZoom: number): Frame {
  const uw = Math.max(1, w - 2 * HUD_SIDE_PX) * THREAT_FILL;
  const uh = Math.max(1, h - HUD_TOP_PX - HUD_BOTTOM_PX) * THREAT_FILL;
  const fit = Math.min(uw / Math.max(1e-3, Math.abs(head.x - target.x)), uh / Math.max(1e-3, Math.abs(head.y - target.y)));
  const lo = Math.min(THREAT_MAX_ZOOM, Math.max(THREAT_MIN_ZOOM, minZoom));
  const zoom = Math.max(lo, Math.min(THREAT_MAX_ZOOM, fit));
  // half-way to the head, but never so far that the target leaves the inner 60%
  const lean = (d: number, room: number) => Math.max(-room, Math.min(room, d / 2));
  const x = target.x + lean(head.x - target.x, uw / 2 / zoom);
  const y = target.y + lean(head.y - target.y, uh / 2 / zoom);
  // centred in the free area (the bottom strip is taller than the top bar)
  return { x, y: y + (HUD_BOTTOM_PX - HUD_TOP_PX) / 2 / zoom, zoom };
}
